import { Fragment, useCallback, useEffect, useMemo, useRef } from "react";

import {
  Button,
  ButtonIconWithLabel,
  Icon_Share16,
  Modal,
  useManagedModal,
} from "@shapr3d/frontend-lib";
import { useSearchParams } from "react-router-dom";

import { ModalIds } from "../../../../constants/modalIds";
import useAccount, { type UserID } from "../../../../hooks/useAccount";
import { useIntersectionObserver } from "../../../../hooks/useIntersectionObserver";
import {
  type ProjectMember,
  ProjectUserRoleDisplayName,
} from "../../../../hooks/useProjectSharing";
import {
  type ProjectViewLinkInvitation,
  type ProjectViewLinkInvitationID,
  type ProjectViewLinkMember,
  useMutateDeleteProjectViewLinkInvitation,
  useMutateDeleteProjectViewLinkUser,
  useProjectViewLinkSharing,
} from "../../../../hooks/useProjectViewLink";
import { useTracker } from "../../../../hooks/useTracker";
import { useWebExport } from "../../../../hooks/useWebExport";
import { ElementClickAction, ElementClickLocation } from "../../../../lib/TrackingAdapter";
import { ProjectRoleUtils, SearchParam, SearchParamIntent } from "../../../../types";
import { OverlayProvider as TooltipPortalOverlayProvider } from "../../../InfoTooltip/InfoTooltip";
import { CopyLinkButton } from "./components/CopyLinkButton";
import type { EmailInvitationFormHandle } from "./components/EmailInvitationForm";
import { EmailInvitationFormViewLink } from "./components/EmailInvitationFormViewLink";
import { GeneralAccessViewLink } from "./components/GeneralAccessViewLink";
import { InvitationRowViewLink } from "./components/InvitationRowViewLink";
import { MemberRowViewLink } from "./components/MemberRowViewLink";
import { OverlayProvider as MenuPortalOverlayProvider } from "./components/MenuPortal";
import { RemoveConfirmationModal } from "./components/RemoveConfirmationModal";
import { Separator } from "./components/Separator";
import { ShareList } from "./components/ShareList";
import type { ShareRowRef } from "./components/ShareRow";
import { ShareRow } from "./components/ShareRow";
import { SpaceRow } from "./components/SpaceRow";

import style from "./ShareViewLinkModal.module.css";

export const ShareViewLinkModal: React.FC<{ hideButton?: boolean }> = ({ hideButton = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: webExportData, viewLinkId } = useWebExport();
  const { data: accountData } = useAccount();
  const tracker = useTracker();
  const userToRemoveRef = useRef<UserID | null>(null);
  const invitationToRemoveRef = useRef<ProjectViewLinkInvitationID | null>(null);
  const emailListContainerRef = useRef<HTMLDivElement>(null);
  const sharingRowRefs = useRef<ShareRowRef[]>([]);
  const emailInvitationFormRef = useRef<EmailInvitationFormHandle>(null);
  const {
    data: projectViewLinkSharingData,
    refetch: refetchProjectViewLinkSharing,
    isLoading: isProjectViewLinkSharingLoading,
  } = useProjectViewLinkSharing();

  const consumeShareIntent = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete(SearchParam.Intent);
        return newSearchParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const isOwner = Boolean(
    webExportData && ProjectRoleUtils.isAtLeast(webExportData.userRole, "owner")
  );

  const shareModal = useManagedModal({ id: ModalIds.SHARE_VIEW_LINK });

  const onClose = useCallback(() => {
    consumeShareIntent();
    shareModal.close();
    tracker.trackShareModalVisibilityChanged({
      viewLinkId,
      webExportId: webExportData?.webExport.webExportId,
      sharedResource: "review_link",
      visible: false,
    });
  }, [tracker, viewLinkId, consumeShareIntent, webExportData, shareModal]);

  const removeProjectViewLinkUserModal = useManagedModal();
  const removeProjectViewLinkInvitationModal = useManagedModal();
  const {
    mutateAsync: mutateDeleteProjectViewLinkUser,
    isPending: isDeleteProjectViewLinkUserPending,
    isError: isDeleteProjectViewLinkUserError,
  } = useMutateDeleteProjectViewLinkUser({
    onSuccess: async () => {
      removeProjectViewLinkUserModal.close();
    },
  });
  const {
    mutateAsync: mutateDeleteProjectViewLinkInvitation,
    isPending: isDeleteProjectViewLinkInvitationPending,
    isError: isDeleteProjectViewLinkInvitationError,
  } = useMutateDeleteProjectViewLinkInvitation({
    onSuccess: async () => {
      removeProjectViewLinkInvitationModal.close();
    },
  });

  const containerIntersectionObserver = useIntersectionObserver(
    emailListContainerRef,
    sharingRowRefs
  );

  useEffect(() => {
    if (
      hideButton ||
      searchParams.get(SearchParam.Intent) !== SearchParamIntent.Share ||
      isProjectViewLinkSharingLoading
    ) {
      return;
    }
    shareModal.open();
  }, [hideButton, searchParams, shareModal, isProjectViewLinkSharingLoading]);

  const sortedUsers = useMemo(() => {
    if (!projectViewLinkSharingData) return [];

    const emails = new Set<string>();

    projectViewLinkSharingData.invitations.forEach(({ email }) => emails.add(email));
    projectViewLinkSharingData.members.forEach(({ email }) => emails.add(email));
    projectViewLinkSharingData.projectUsers.forEach(({ email }) => emails.add(email));

    const combinedEntries = new Map<
      string,
      {
        email: string;
        viewLinkInvitation?: ProjectViewLinkInvitation;
        viewLinkUser?: ProjectViewLinkMember;
        projectUser?: ProjectMember;
      }
    >();

    emails.forEach((email) => {
      combinedEntries.set(email, { email });
    });

    projectViewLinkSharingData.invitations.forEach((invitation) => {
      const entry = combinedEntries.get(invitation.email);
      if (!entry) return;
      entry.viewLinkInvitation = invitation;
    });

    projectViewLinkSharingData.members.forEach((member) => {
      const entry = combinedEntries.get(member.email);
      if (!entry) return;
      entry.viewLinkUser = member;
    });

    projectViewLinkSharingData.projectUsers.forEach((projectUser) => {
      const entry = combinedEntries.get(projectUser.email);
      if (!entry) return;
      if (
        entry.viewLinkUser &&
        !ProjectRoleUtils.isAtLeast(entry.viewLinkUser.role, projectUser.role)
      ) {
        delete entry.viewLinkUser;
      }
      if (
        entry.viewLinkInvitation &&
        !ProjectRoleUtils.isAtLeast(entry.viewLinkInvitation.role, projectUser.role)
      ) {
        delete entry.viewLinkInvitation;
      }
      entry.projectUser = projectUser;
    });
    const sortedUsers = Array.from(combinedEntries.values());

    const isNonInvitedUser = (entry: (typeof sortedUsers)[number]) => {
      const hasViewLinkUser = Boolean(entry.viewLinkUser);
      const doesOnlyHaveProjectUser =
        Boolean(entry.projectUser) && !entry.viewLinkInvitation && !entry.viewLinkUser;
      return hasViewLinkUser || doesOnlyHaveProjectUser;
    };

    const invitedUsers = sortedUsers.filter((entry) => !isNonInvitedUser(entry));
    const nonInvitedUsers = sortedUsers.filter((entry) => isNonInvitedUser(entry));

    nonInvitedUsers.sort((a, b) => {
      return a.email.localeCompare(b.email);
    });
    return [...invitedUsers, ...nonInvitedUsers];
  }, [projectViewLinkSharingData]);

  return (
    <>
      {!hideButton && (
        <Button
          variant="soft"
          onClick={async () => {
            try {
              await refetchProjectViewLinkSharing();
              shareModal.open();
              tracker.trackShareModalVisibilityChanged({
                viewLinkId,
                webExportId: webExportData?.webExport.webExportId,
                sharedResource: "review_link",
                visible: true,
              });
            } catch {
              // ignore error
            }
          }}
        >
          <ButtonIconWithLabel icon={<Icon_Share16 />} label="Share" />
        </Button>
      )}
      {projectViewLinkSharingData && (
        <Modal
          {...shareModal.props}
          onClose={onClose}
          title={"Share this version"}
          className={style.modal}
          onOpen={() => emailInvitationFormRef.current?.focus()}
          onCloseStart={() => {
            emailInvitationFormRef.current?.blur();
          }}
        >
          <MenuPortalOverlayProvider>
            <TooltipPortalOverlayProvider>
              <div className={style.container}>
                {projectViewLinkSharingData && (
                  <>
                    <EmailInvitationFormViewLink ref={emailInvitationFormRef} />
                    <span className={style.sectionTitle}>Who has access</span>
                    <div className={style.accessContainer}>
                      <GeneralAccessViewLink />
                      {(() => {
                        if (!webExportData || !projectViewLinkSharingData) {
                          return null;
                        }

                        if (!isOwner || !projectViewLinkSharingData.space) {
                          return (
                            <SpaceRow
                              variant="no-access"
                              projectName={webExportData.webExport?.title ?? "Untitled Project"}
                            />
                          );
                        }

                        if (projectViewLinkSharingData.space.spaceType === "private") {
                          return null;
                        }

                        return (
                          <SpaceRow
                            variant="owner-team"
                            space={projectViewLinkSharingData.space}
                            projectName={webExportData.webExport?.title}
                          />
                        );
                      })()}
                    </div>
                    <ShareList className={style.emailListContainer} ref={emailListContainerRef}>
                      {sortedUsers.map((entry, index) => {
                        const invitation = entry.viewLinkInvitation;
                        const member = entry.viewLinkUser;
                        const projectUser = entry.projectUser;
                        return (
                          <Fragment key={entry.email}>
                            <Separator />
                            {invitation ? (
                              <InvitationRowViewLink
                                ref={(ref) => {
                                  if (ref) {
                                    sharingRowRefs.current[index + 1] = ref;
                                  }
                                }}
                                invitation={invitation}
                                projectUser={entry.projectUser}
                                containerIntersectionObserver={containerIntersectionObserver}
                                onRemove={(invitationId) => {
                                  invitationToRemoveRef.current = invitationId;
                                  removeProjectViewLinkInvitationModal.open();
                                }}
                                tooltipText={
                                  entry.projectUser
                                    ? `This person has access through permissions inherited from the project as a ${ProjectUserRoleDisplayName(entry.projectUser.role)}.`
                                    : undefined
                                }
                              />
                            ) : member ? (
                              <MemberRowViewLink
                                ref={(ref) => {
                                  if (ref) {
                                    sharingRowRefs.current[index + 1] = ref;
                                  }
                                }}
                                member={member}
                                projectUser={entry.projectUser}
                                containerIntersectionObserver={containerIntersectionObserver}
                                onRemove={(userId) => {
                                  userToRemoveRef.current = userId;
                                  removeProjectViewLinkUserModal.open();
                                }}
                                isCurrentUser={member.userId === accountData?.id}
                                tooltipText={
                                  entry.projectUser
                                    ? `This person has access through permissions inherited from the project as a ${ProjectUserRoleDisplayName(entry.projectUser.role)}.`
                                    : undefined
                                }
                              />
                            ) : projectUser ? (
                              <ShareRow
                                ref={(ref) => {
                                  if (ref) {
                                    sharingRowRefs.current[index + 1] = ref;
                                  }
                                }}
                                roles={[projectUser.role]}
                                roleDisplayName={ProjectUserRoleDisplayName}
                                entity={projectUser}
                                immutable
                                sharingBadges={[
                                  ...(projectUser.isTeamUser ? [] : ["Guest" as const]),
                                ]}
                                containerIntersectionObserver={containerIntersectionObserver}
                                tooltipText={
                                  "This person has access through permissions inherited from the project."
                                }
                              />
                            ) : null}
                          </Fragment>
                        );
                      })}
                    </ShareList>
                  </>
                )}

                <div className={style.footerContainer}>
                  <CopyLinkButton
                    onCopy={() => {
                      tracker.trackButtonClick({
                        action: ElementClickAction.COPY_LINK,
                        location: ElementClickLocation.SHARE_MODAL,
                        details: "review_link",
                      });
                    }}
                  />
                </div>
              </div>
            </TooltipPortalOverlayProvider>
          </MenuPortalOverlayProvider>
        </Modal>
      )}
      <RemoveConfirmationModal
        entity={userToRemoveRef.current === accountData?.id ? "version" : "user"}
        modalProps={removeProjectViewLinkUserModal.props}
        isPending={isDeleteProjectViewLinkUserPending}
        isError={isDeleteProjectViewLinkUserError}
        onConfirm={async () => {
          if (!userToRemoveRef.current) return;
          tracker.trackShareModalMemberRemoveConfirmed({
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
            affectedUserId: userToRemoveRef.current,
            sharedResource: "review_link",
          });
          return mutateDeleteProjectViewLinkUser({ userId: userToRemoveRef.current });
        }}
        action={userToRemoveRef.current === accountData?.id ? "leave" : "remove"}
      />
      <RemoveConfirmationModal
        entity={"invitation"}
        modalProps={removeProjectViewLinkInvitationModal.props}
        isPending={isDeleteProjectViewLinkInvitationPending}
        isError={isDeleteProjectViewLinkInvitationError}
        onConfirm={async () => {
          if (!invitationToRemoveRef.current) return;
          tracker.trackShareModalMemberRemoveConfirmed({
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
            invitationId: invitationToRemoveRef.current,
            sharedResource: "review_link",
          });
          return mutateDeleteProjectViewLinkInvitation({
            invitationId: invitationToRemoveRef.current,
          });
        }}
      />
    </>
  );
};
